/**
 * Account deletion — the client half of the 30-day grace period (Phase 118-07).
 *
 * Server: `supabase/functions/account-deletion` (118-05), one POST with `action`:
 *   status   AAL1  200 { deletion: { status, scheduled_for } | null }
 *   preview  AAL2  200 { preview }
 *   request  AAL2  202 { request, created, signed_out }
 *                  207 { …, failed, retry: true }   recorded, not finished
 *                  409 already_scheduled            finished earlier
 *   cancel   AAL2  200 { cancelled, billing, keys_restored: false }
 *                  207 … billing restore failed (retried on the next call)
 *                  409 no_pending_request | erasure_in_progress
 * Refusals are { error, code }. This module branches on `code` and the HTTP
 * status, NEVER on message text.
 *
 * Two rules from the retention policy (:88) and 118-05 shape everything here:
 *   - a 207 is NOT success. It means the request row exists but a grace step
 *     (revoking keys, pausing billing, the email, the sign-out) did not hold.
 *     The server kept the session alive precisely so the client can call
 *     `request` again; it converges.
 *   - the pending gate's answer comes from the server, never a local cache:
 *     routing on cached state is how one user's state bleeds into the next.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { isBackendEnabled, isMockMode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { APP_LOCALE } from '@/lib/format';

// -----------------------------------------------------------------------------
// Shapes
// -----------------------------------------------------------------------------

/** Statuses of a request that still describes the account (server OPEN_STATUSES). */
export type OpenDeletionStatus = 'pending' | 'processing' | 'partial' | 'failed';

export interface DeletionState {
  status: OpenDeletionStatus;
  /** ISO time the erasure runs. */
  scheduledFor: string;
}

/** What the last founder chooses for their company (119-06). */
export const COMPANY_OUTCOMES = ['closed', 'acquired', 'bankrupt', 'delete_data'] as const;
export type CompanyOutcome = (typeof COMPANY_OUTCOMES)[number];

/** The server's own codes for what a choice keeps / deletes (preview `choices`). */
export interface CompanyOutcomeChoice {
  outcome: CompanyOutcome;
  keeps: string[];
  deletes: string[];
  /** Fields the choice needs (acquired: acquirer_name). */
  requires: string[];
}

export interface DeletionPreviewCompany {
  startupId: string;
  name: string | null;
  /** transferred: a co-founder keeps it; unclaimed: you are its last member —
   *  it is CLOSED with the outcome you choose (default closed), its data
   *  sealed, deleted only if you choose delete_data; left: others run it,
   *  you are removed. */
  action: 'transferred' | 'unclaimed' | 'left';
  newOwnerName: string | null;
  /** unclaimed only: the outcome used when none is chosen. */
  defaultOutcome: CompanyOutcome | null;
  /** unclaimed only: what may be chosen, in the server's order. */
  choices: CompanyOutcomeChoice[];
}

/** The person's choice for one company. */
export interface CompanyOutcomeSelection {
  outcome: CompanyOutcome;
  /** acquired only: the acquiring COMPANY's name (never a person). */
  acquirerName?: string;
}

export interface DeletionPreview {
  companies: DeletionPreviewCompany[];
  subscription: { tier: string; renewsAt: string | null; cancelAtPeriodEnd: boolean } | null;
  apiKeys: number;
  oauthClients: number;
  scheduledForIfRequested: string;
}

export interface DeletionScheduled {
  scheduledFor: string;
  /** True when this call found a request already fully applied (409). */
  alreadyScheduled: boolean;
}

export type BillingRestore = 'untouched' | 'unchanged' | 'restored' | 'ended';

export interface DeletionCancelled {
  billing: BillingRestore | null;
  /** Always false: revoked API keys and connected apps are not reinstated. */
  keysRestored: false;
  /** The billing restore failed and is retried on the next cancel call. */
  billingRetryPending: boolean;
}

// -----------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------

export type AccountDeletionErrorKind =
  | 'step_up_required'
  | 'partial'
  | 'demo_account'
  | 'rate_limited'
  | 'erasure_in_progress'
  | 'no_pending_request'
  | 'confirmation_required'
  | 'invalid_company_outcomes'
  | 'unavailable_offline'
  | 'invalid_response'
  | 'failed';

/** A refusal or failure, carrying the message written for the person. */
export class AccountDeletionError extends Error {
  readonly kind: AccountDeletionErrorKind;
  readonly userMessage: string;
  readonly status?: number;

  constructor(kind: AccountDeletionErrorKind, userMessage: string, detail?: string, status?: number) {
    super(`account_deletion_${kind}${detail ? `: ${detail}` : ''}`);
    this.name = 'AccountDeletionError';
    this.kind = kind;
    this.userMessage = userMessage;
    this.status = status;
  }
}

/** 403 `aal2_required`: run the MFA step-up, then retry the same action. */
export class StepUpRequiredError extends AccountDeletionError {
  constructor(status = 403) {
    super(
      'step_up_required',
      'For your security, confirm it is you with the code from your authenticator app.',
      'aal2_required',
      status,
    );
    this.name = 'StepUpRequiredError';
  }
}

/** 207 on `request`: recorded, not finished. Never shown as success. */
export class PartialRequestError extends AccountDeletionError {
  readonly failed: string[];
  readonly scheduledFor: string | null;

  constructor(failed: string[], scheduledFor: string | null) {
    super(
      'partial',
      'Your deletion request is recorded but not finished. Tap Retry to complete it — nothing is deleted twice.',
      failed.join(','),
      207,
    );
    this.name = 'PartialRequestError';
    this.failed = failed;
    this.scheduledFor = scheduledFor;
  }
}

const GENERIC = 'Your request could not be completed. Please try again.';

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

/** Map a non-2xx status + body to the typed error. Pure; exported for tests. */
export function mapRefusal(status: number, body: Record<string, unknown>): AccountDeletionError {
  const code = typeof body.code === 'string' ? body.code : null;
  switch (code) {
    case 'aal2_required':
      return new StepUpRequiredError(status);
    case 'demo_account':
      return new AccountDeletionError(
        'demo_account',
        'This is a shared demo account, used by everyone who tries Inverio, so it cannot be deleted.',
        code,
        status,
      );
    case 'rate_limited':
      return new AccountDeletionError(
        'rate_limited',
        'Too many deletion requests today. Please try again tomorrow.',
        code,
        status,
      );
    case 'erasure_in_progress':
      return new AccountDeletionError(
        'erasure_in_progress',
        'Your account deletion is already being carried out and can no longer be cancelled.',
        code,
        status,
      );
    case 'no_pending_request':
      return new AccountDeletionError(
        'no_pending_request',
        'There is no pending deletion to cancel — your account is active.',
        code,
        status,
      );
    case 'confirmation_required':
      return new AccountDeletionError('confirmation_required', 'Type DELETE to confirm.', code, status);
    case 'invalid_company_outcomes':
      return new AccountDeletionError(
        'invalid_company_outcomes',
        'The choice for one of your companies could not be accepted. Review the choices and try again.',
        code,
        status,
      );
    default:
      return new AccountDeletionError('failed', GENERIC, code ?? `http_${status}`, status);
  }
}

// -----------------------------------------------------------------------------
// Transport
// -----------------------------------------------------------------------------

type Action = 'status' | 'preview' | 'request' | 'cancel';

interface Reply {
  status: number;
  body: Record<string, unknown>;
}

/**
 * One call. 2xx resolves with the status (functions.invoke hides it except on
 * `response`), and every non-2xx is read off `error.context` — the only place
 * functions.invoke leaves the body that carries the `code`.
 */
async function call(action: Action, extra: Record<string, unknown> = {}): Promise<Reply> {
  if (!isBackendEnabled() || isMockMode()) {
    throw new AccountDeletionError(
      'unavailable_offline',
      'Account deletion needs a live account. It is not available in offline sample mode.',
    );
  }
  const { data, error, response } = await supabase.functions.invoke('account-deletion', {
    body: { action, ...extra },
  });
  if (!error) return { status: response?.status ?? 200, body: asRecord(data) };

  const err = error as { name?: unknown; message?: unknown; context?: { status?: unknown; json?: () => Promise<unknown> } };
  if (err.name !== 'FunctionsHttpError' || typeof err.context?.status !== 'number') {
    // Network / relay failure: no status to branch on.
    throw new AccountDeletionError('failed', GENERIC, typeof err.message === 'string' ? err.message : String(error));
  }
  let body: Record<string, unknown> = {};
  try {
    body = asRecord(await err.context.json?.());
  } catch (parseErr) {
    // The status alone still decides the message.
    reportError(parseErr, { context: `accountDeletion.${action}.errorBody`, level: 'warning' });
  }
  return { status: err.context.status, body };
}

function invalid(detail: string): AccountDeletionError {
  return new AccountDeletionError('invalid_response', GENERIC, detail);
}

// -----------------------------------------------------------------------------
// Parsers (pure; exported for tests)
// -----------------------------------------------------------------------------

const OPEN: readonly string[] = ['pending', 'processing', 'partial', 'failed'];

export function parseStatus(body: Record<string, unknown>): DeletionState | null {
  if (!('deletion' in body)) throw invalid('status: missing deletion');
  const d = body.deletion;
  if (d === null) return null;
  const rec = asRecord(d);
  if (typeof rec.status !== 'string' || typeof rec.scheduled_for !== 'string') throw invalid('status: bad shape');
  // A closed status (cancelled/completed) does not describe this account.
  if (!OPEN.includes(rec.status)) return null;
  return { status: rec.status as OpenDeletionStatus, scheduledFor: rec.scheduled_for };
}

export function parsePreview(body: Record<string, unknown>): DeletionPreview {
  const p = asRecord(body.preview);
  if (!Array.isArray(p.companies) || typeof p.scheduled_for_if_requested !== 'string') {
    throw invalid('preview: bad shape');
  }
  const companies: DeletionPreviewCompany[] = p.companies.map((c) => {
    const r = asRecord(c);
    const action = r.action === 'transferred' || r.action === 'unclaimed' ? r.action : 'left';
    const choices = action === 'unclaimed' ? parseChoices(r.choices) : [];
    // An unclaimed company always has a choice; a preview without one is
    // malformed, and showing no choice would hide what is about to happen.
    if (action === 'unclaimed' && choices.length === 0) throw invalid('preview: unclaimed company without choices');
    return {
      startupId: String(r.startup_id ?? ''),
      name: typeof r.name === 'string' && r.name.trim() ? r.name : null,
      action,
      newOwnerName: typeof r.new_owner_name === 'string' && r.new_owner_name.trim() ? r.new_owner_name : null,
      defaultOutcome: action === 'unclaimed' && isCompanyOutcome(r.default_outcome) ? r.default_outcome : action === 'unclaimed' ? 'closed' : null,
      choices,
    };
  });
  const sub = p.subscription ? asRecord(p.subscription) : null;
  return {
    companies,
    subscription: sub
      ? {
          tier: String(sub.tier ?? ''),
          renewsAt: typeof sub.renews_at === 'string' ? sub.renews_at : null,
          cancelAtPeriodEnd: sub.cancel_at_period_end === true,
        }
      : null,
    apiKeys: typeof p.api_keys === 'number' ? p.api_keys : 0,
    oauthClients: typeof p.oauth_clients === 'number' ? p.oauth_clients : 0,
    scheduledForIfRequested: p.scheduled_for_if_requested,
  };
}

export function isCompanyOutcome(v: unknown): v is CompanyOutcome {
  return typeof v === 'string' && (COMPANY_OUTCOMES as readonly string[]).includes(v);
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** The preview's choices, unknown outcomes dropped. Pure; exported for tests. */
export function parseChoices(raw: unknown): CompanyOutcomeChoice[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((c) => {
    const r = asRecord(c);
    if (!isCompanyOutcome(r.outcome)) return [];
    return [{ outcome: r.outcome, keeps: strings(r.keeps), deletes: strings(r.deletes), requires: strings(r.requires) }];
  });
}

/** acquired: the acquiring company's name, 1–200 characters, no control characters (server rule). */
export const ACQUIRER_NAME_MAX = 200;
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

export function acquirerNameProblem(name: string | undefined): string | null {
  const n = (name ?? '').trim();
  if (n.length === 0) return "Enter the acquiring company's name.";
  if (n.length > ACQUIRER_NAME_MAX) return `Keep the name under ${ACQUIRER_NAME_MAX} characters.`;
  if (CONTROL_CHARS.test(n)) return 'The name contains characters that are not allowed.';
  return null;
}

export type CompanyOutcomesBody = Record<string, CompanyOutcome | { outcome: 'acquired'; acquirer_name: string }>;

/**
 * The `company_outcomes` body for the companies the preview offers a choice
 * for — and only those (the server refuses any other id). A company with no
 * selection gets its default (closed), so the request always states every
 * choice explicitly. Returns the problems instead when a selection is not
 * complete (an acquired company with no acquirer name). Pure.
 */
export function buildCompanyOutcomes(
  companies: readonly DeletionPreviewCompany[],
  selections: Readonly<Record<string, CompanyOutcomeSelection | undefined>>,
): { ok: true; body: CompanyOutcomesBody } | { ok: false; problems: Record<string, string> } {
  const body: CompanyOutcomesBody = {};
  const problems: Record<string, string> = {};
  for (const c of companies) {
    if (c.action !== 'unclaimed' || c.choices.length === 0) continue;
    const id = c.startupId.toLowerCase();
    const sel = selections[c.startupId] ?? selections[id];
    const outcome = sel?.outcome ?? c.defaultOutcome ?? 'closed';
    if (!c.choices.some((ch) => ch.outcome === outcome)) {
      problems[c.startupId] = 'Choose one of the options.';
      continue;
    }
    if (outcome === 'acquired') {
      const problem = acquirerNameProblem(sel?.acquirerName);
      if (problem) {
        problems[c.startupId] = problem;
        continue;
      }
      body[id] = { outcome: 'acquired', acquirer_name: (sel?.acquirerName ?? '').trim() };
    } else {
      body[id] = outcome;
    }
  }
  return Object.keys(problems).length > 0 ? { ok: false, problems } : { ok: true, body };
}

/** The scheduled date of a 2xx `request` — or of the legacy data-delete 202. */
function scheduledForOf(body: Record<string, unknown>): string | null {
  const req = asRecord(body.request);
  if (typeof req.scheduled_for === 'string') return req.scheduled_for;
  if (typeof body.scheduled_for === 'string') return body.scheduled_for; // legacy 202 { scheduled_for }
  return null;
}

/**
 * Interpret one `request` reply. Pure; exported for tests.
 *   202 → scheduled          207 / `failed` present → PartialRequestError
 *   409 already_scheduled → scheduled (applied by an earlier call)
 *   anything else → the typed refusal
 */
export function interpretRequestReply({ status, body }: Reply): DeletionScheduled {
  if (status >= 200 && status < 300) {
    const scheduledFor = scheduledForOf(body);
    const failed = Array.isArray(body.failed) ? body.failed.map(String) : [];
    if (status === 207 || failed.length > 0 || body.retry === true) {
      throw new PartialRequestError(failed, scheduledFor);
    }
    if (!scheduledFor) throw invalid('request: missing scheduled_for');
    return { scheduledFor, alreadyScheduled: false };
  }
  if (status === 409 && body.code === 'already_scheduled' && typeof body.scheduled_for === 'string') {
    return { scheduledFor: body.scheduled_for, alreadyScheduled: true };
  }
  throw mapRefusal(status, body);
}

export function interpretCancelReply({ status, body }: Reply): DeletionCancelled {
  if (status >= 200 && status < 300) {
    if (body.cancelled !== true) throw invalid('cancel: not cancelled');
    const billing = typeof body.billing === 'string' ? (body.billing as BillingRestore) : null;
    return { billing, keysRestored: false, billingRetryPending: body.retry === true };
  }
  throw mapRefusal(status, body);
}

// -----------------------------------------------------------------------------
// Calls
// -----------------------------------------------------------------------------

export async function fetchDeletionStatus(): Promise<DeletionState | null> {
  const reply = await call('status');
  if (reply.status !== 200) throw mapRefusal(reply.status, reply.body);
  return parseStatus(reply.body);
}

export async function fetchDeletionPreview(): Promise<DeletionPreview> {
  const reply = await call('preview');
  if (reply.status !== 200) throw mapRefusal(reply.status, reply.body);
  return parsePreview(reply.body);
}

/** How many times one tap sends `request` before handing a 207 to the person. */
export const REQUEST_ATTEMPTS = 2;

/**
 * Schedule the deletion. A 207 is retried at once with the same session (the
 * request is idempotent — never a second row); if it still has not converged,
 * the PartialRequestError reaches the screen, which offers Retry.
 */
export async function requestAccountDeletion(
  companyOutcomes: CompanyOutcomesBody = {},
  attempts: number = REQUEST_ATTEMPTS,
): Promise<DeletionScheduled> {
  let last: PartialRequestError | null = null;
  const extra: Record<string, unknown> = { confirm: 'DELETE' };
  if (Object.keys(companyOutcomes).length > 0) extra.company_outcomes = companyOutcomes;
  for (let i = 0; i < Math.max(1, attempts); i++) {
    try {
      return interpretRequestReply(await call('request', extra));
    } catch (err) {
      if (!(err instanceof PartialRequestError)) throw err;
      last = err;
    }
  }
  throw last ?? new AccountDeletionError('failed', GENERIC);
}

export async function cancelAccountDeletion(): Promise<DeletionCancelled> {
  return interpretCancelReply(await call('cancel'));
}

/** The typed confirmation: exactly DELETE, case-sensitive, surrounding space ignored. */
export function isDeleteConfirmation(text: string): boolean {
  return text.trim() === 'DELETE';
}

/**
 * After a scheduled request the server has already ended every session, so
 * the local side must follow — completely. `signOut()` is the ONE purge: it
 * clears the React Query cache, runs the inverio-store reset cascade (every
 * persisted user-scoped store), resets the profile/subscription/score stores
 * and deletes the Drive/Dropbox tokens from SecureStore. Its server revocation
 * fails here (the session is gone); it reports that and does not throw.
 *
 * Never throws: the screen must reach its "scheduled" state either way. The
 * caller leaves afterwards — on web with `location.replace('/')`, which also
 * drops in-memory module state.
 */
export async function purgeLocalSession(signOut: () => Promise<unknown>): Promise<void> {
  try {
    await signOut();
  } catch (err) {
    reportError(err, { context: 'purgeLocalSession.signOut' });
  }
}

/** What the person is told after a cancel. Pure. */
export function cancelledMessage(r: DeletionCancelled): string {
  const parts = ['Your account is active again. API keys and connected apps were revoked and are not restored — re-create any you need.'];
  if (r.billing === 'restored') parts.push('Your subscription will renew as before.');
  if (r.billing === 'ended') parts.push('Your subscription ended during this time; you can choose a plan again from Settings.');
  if (r.billingRetryPending) parts.push('We could not update your subscription yet; tap Cancel deletion again later if it still shows as ending.');
  return parts.join(' ');
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days left until `iso` (never negative; a partial day counts as one). */
export function daysUntil(iso: string, nowMs: number = Date.now()): number {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.ceil((t - nowMs) / DAY_MS));
}

/** "15 November 2026" in the device locale. */
export function formatDeletionDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return 'the scheduled date';
  return d.toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

/** Shared by the pending gate and the screens. Per user: a key without the id
 *  could serve one person's deletion state to the next on the same device. */
export const deletionStatusKey = (userId: string | null | undefined) =>
  ['account-deletion', 'status', userId ?? 'anonymous'] as const;

/** Can this session have a server-side deletion state at all? */
export function deletionBackendAvailable(): boolean {
  return isBackendEnabled() && !isMockMode();
}

/** Retry only what can change on its own: a refusal (step-up, demo account,
 *  offline mode) answers the same way every time, so retrying only delays it. */
export function retryTransient(failureCount: number, err: unknown): boolean {
  if (err instanceof AccountDeletionError && err.kind !== 'failed') return false;
  return failureCount < 2;
}

export function useDeletionStatus(userId: string | null | undefined) {
  return useQuery<DeletionState | null>({
    queryKey: deletionStatusKey(userId),
    queryFn: fetchDeletionStatus,
    enabled: !!userId && deletionBackendAvailable(),
    staleTime: 60_000,
    retry: retryTransient,
  });
}

export function useDeletionPreview(enabled: boolean) {
  return useQuery<DeletionPreview>({
    queryKey: ['account-deletion', 'preview'],
    queryFn: fetchDeletionPreview,
    enabled: enabled && deletionBackendAvailable(),
    staleTime: 0,
    retry: retryTransient,
  });
}

export function useRequestDeletion() {
  return useMutation<DeletionScheduled, Error, CompanyOutcomesBody | undefined>({
    mutationFn: (outcomes) => requestAccountDeletion(outcomes ?? {}),
  });
}

// -----------------------------------------------------------------------------
// What each choice keeps and deletes, in words (the preview's codes)
// -----------------------------------------------------------------------------

export const OUTCOME_LABEL: Record<CompanyOutcome, { title: string; summary: string }> = {
  closed: { title: 'Close the company', summary: 'It stays listed as closed. Its data is sealed, not deleted.' },
  acquired: { title: 'It was acquired', summary: 'It stays listed as acquired. Its data is sealed, not deleted. We record the company you name.' },
  bankrupt: { title: 'It went bankrupt', summary: 'It stays listed as bankrupt. Its data is sealed, not deleted.' },
  delete_data: {
    title: "Close and delete the company's data",
    summary: 'It stays listed as closed with its score history; its data is deleted.',
  },
};

const KEEP_TEXT: Record<string, string> = {
  company_page: 'The company page, marked with its status',
  score_history: 'Its score history, in full',
  company_data_sealed: 'Its data, sealed: nobody can read it, and it is deleted only on request',
  fundraising_rounds: 'Its fundraising rounds (amounts and dates)',
  acquirer: "The acquiring company's name",
};

const DELETE_TEXT: Record<string, string> = {
  interview_transcripts: 'Interview transcripts',
  data_room: 'Data room documents',
  claims: 'Claims and their verifications',
  analyses: 'Analyses, valuations and team insights',
  company_texts: "The company's descriptions",
};

/** The words for a keeps / deletes code. An unknown code is never shown as a blank line. */
export function keepText(code: string): string {
  return KEEP_TEXT[code] ?? code.replace(/_/g, ' ');
}
export function deleteText(code: string): string {
  return DELETE_TEXT[code] ?? code.replace(/_/g, ' ');
}

export function useCancelDeletion(userId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<DeletionCancelled, Error>({
    mutationFn: cancelAccountDeletion,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: deletionStatusKey(userId) });
    },
  });
}
